import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, mergeProps as _mergeProps, withCtx as _withCtx, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex cos-both-form-item" }

import { ref, reactive, onMounted, watch, watchEffect, toRaw, computed } from 'vue'
import { cloneDeep, isEqual } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import ClueAndCustomerSelect from '@/components/ClueAndCustomerSelect/index.vue'
import GcCodeSelect from '@/components/GcCodeSelect'
import CosSingleDateTimePicker from '@/components/CosSingleDateTimePicker/index.vue'
import { selectForNumberValidate } from '@/utils/validate'
import { Form } from 'ant-design-vue'
import { businessStatusEnum } from '@/views/overseasSales/commonEnum/dataConfig'
import { listByParamCodes } from '@/api/common'
import { paramCodesDataFilter } from '@/utils/index'
import type { ParamCodeItem } from '@/typings/common'
import type { SelectProps } from 'ant-design-vue'
import type { FbuBusinessEditParams } from '@/typings/api/chanceManage'
import { potentialDetail } from '@/api/overseasSales/customerCenter'
import CosInput from '@/components/Antd/CosInput/index.vue'

const __default__ = { name: 'BusinessForm' }


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    value: {},
    title: {},
    type: {},
    saleChanceRefId: { type: Boolean },
    arkCodeId: { type: Boolean },
    isFromPotentialCustomer: { type: Boolean }
  },
  emits: ['update:value'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const useForm = Form.useForm
const { t } = useI18n()

const props = __props

const businessStatusEnumFilter = computed(() => {
  return businessStatusEnum.map((item) => ({ ...item, disabled: props.type === 'add' && item.value === 5 }))
})
const isEdit = computed(() => props.type !== 'view')
const logisticsProductsEnum = ref<SelectProps['options']>([])
const paramCodeData = ref<{
  ofbu_demand_info: ParamCodeItem[]
  ofbu_currency: ParamCodeItem[]
  ofbu_shipments: ParamCodeItem[]
}>({
  ofbu_demand_info: [], // 区域仓(需求海外仓参数)
  ofbu_currency: [], // 币种
  ofbu_shipments: [] // 发货量
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_ofbu',
    paramCodes: [
      'ofbu_demand_info',
      'ofbu_currency',
      'ofbu_shipments'
    ]
  }).then((res) => {
    if (props.type !== 'view') {
      // 递归过滤业务参数状态
      res.data = paramCodesDataFilter(res.data, 0)
    }
    paramCodeData.value = res.data
  })
}
onMounted(() => getByParamCodes())

const dataForm = reactive<FbuBusinessEditParams>({
  customerName: undefined,
  saleChanceRefId: undefined,
  arkCodeId: undefined,
  planSaleAmount: undefined,
  currency: undefined,
  status: undefined,
  remark: undefined,
  chanceType: undefined,
  orderType: 2,
  planSignTime: undefined,
  headDomesticWarehouse: undefined,
  headDestinationCountryWarehouse: undefined,
  headTransportMode: undefined,
  headCompetitors: undefined,
  tailWarehouse: undefined,
  tailProductName: undefined,
  tailCompetitors: undefined
})

const emit = __emit

watch(
  () => props.value,
  (val) => {
    if (!val) return
    // 如果值相同则不更新
    const isSame = isEqual(toRaw(dataForm), toRaw(val))
    if (isSame) return
    const clone = cloneDeep(val)
    Object.assign(dataForm, clone)
    if (props.type === 'view') {
      setTimeout(() => clearValidate())
    }
  },
  { deep: true, immediate: true }
)
watch(
  dataForm,
  (val) => {
    emit('update:value', val)
  },
  { deep: true }
)

const isRequiredCustomer = ref<boolean>()
const isEmptyCustomer = ref<boolean>()

// 监听是否转潜在客户(客户代码非必填)
// 监听有客户代码数据时则必选
const isFromPotentialCustomer = computed(() => props.isFromPotentialCustomer)
watch(
  [isFromPotentialCustomer, isEmptyCustomer],
  ([isFromPotential, isEmptyCustomer]) => {
    if (isFromPotential || isEmptyCustomer) {
      isRequiredCustomer.value = false
    } else {
      isRequiredCustomer.value = true
    }
    if (props.type === 'edit' || props.type === 'view') {
      setTimeout(() => clearValidate(['arkCodeId']), 0)
    }
  },
  { immediate: true }
)
const handleCustomerNameChange = (val: any) => {
  dataForm.shipments = undefined
  dataForm.planSaleAmount = undefined
  dataForm.currency = undefined
  potentialDetail(val)
    .then((res) => {
      if (res.data?.business) {
        if (res.data.business.shipments) {
          dataForm.shipments = res.data.business.shipments
        }
        if (res.data.business.annualSales) {
          dataForm.planSaleAmount = res.data.business.annualSales
        }
        if (res.data.business.annualSalesUnit) {
          dataForm.currency = res.data.business.annualSalesUnit
        }
      }
    })
  setTimeout(() => clearValidate(['arkCodeId']), 0)
  setTimeout(() => clearValidate(['planSaleAmount']), 0)
  setTimeout(() => clearValidate(['currency']), 0)
}
const formRules = computed(() => ({
  saleChanceRefId: [{ required: true, message: t('common.pleaseSelect') }],
  arkCodeId: [{ required: true, message: t('common.pleaseSelect') }],
  planSaleAmount: [{ required: true, message: t('common.pleaseInput') }],
  currency: [{ required: true, message: t('common.pleaseSelect') }],
  status: [{ required: true, validator: selectForNumberValidate, message: t('common.pleaseSelect') }],
  headDestinationCountryWarehouse: [{ required: true, validator: selectForNumberValidate, message: t('common.pleaseSelect') }]
}))
const { resetFields, validate, validateInfos, clearValidate } = useForm(dataForm, formRules)

__expose({ resetFields, validate, clearValidate })

return (_ctx: any,_cache: any) => {
  const _component_dc_form_item = _resolveComponent("dc-form-item")!
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_CosSelect = _resolveComponent("CosSelect")!
  const _component_ParamCodesSelect = _resolveComponent("ParamCodesSelect")!
  const _component_CosInputNumber = _resolveComponent("CosInputNumber")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_CosTextarea = _resolveComponent("CosTextarea")!
  const _component_dc_layout_card = _resolveComponent("dc-layout-card")!
  const _component_a_form = _resolveComponent("a-form")!

  return (_openBlock(), _createBlock(_component_a_form, { scrollToFirstError: "" }, {
    default: _withCtx(() => [
      _createVNode(_component_dc_layout_card, {
        title: props.title,
        span: 6
      }, {
        default: _withCtx(() => [
          _createVNode(_component_dc_form_item, _mergeProps({
            label: _unref(t)('business.7y5c89')
          }, _unref(validateInfos).saleChanceRefId), {
            default: _withCtx(() => [
              _createVNode(ClueAndCustomerSelect, {
                appId: "cos_ofbu",
                value: dataForm.saleChanceRefId,
                "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((dataForm.saleChanceRefId) = $event)),
                label: dataForm.customerName,
                "onUpdate:label": _cache[1] || (_cache[1] = ($event: any) => ((dataForm.customerName) = $event)),
                disabled: !isEdit.value || props.saleChanceRefId,
                view: props.type === 'view',
                onChange: handleCustomerNameChange
              }, null, 8, ["value", "label", "disabled", "view"])
            ]),
            _: 1
          }, 16, ["label"]),
          _createVNode(_component_dc_form_item, _mergeProps({
            label: _unref(t)('business.nnrhea')
          }, _unref(validateInfos).arkCodeId), {
            default: _withCtx(() => [
              _createVNode(_unref(GcCodeSelect), {
                cosId: dataForm.saleChanceRefId,
                label: dataForm.arkCode,
                "onUpdate:label": _cache[2] || (_cache[2] = ($event: any) => ((dataForm.arkCode) = $event)),
                value: dataForm.arkCodeId,
                "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((dataForm.arkCodeId) = $event)),
                isEmpty: isEmptyCustomer.value,
                "onUpdate:isEmpty": _cache[4] || (_cache[4] = ($event: any) => ((isEmptyCustomer).value = $event)),
                disabled: !isEdit.value || props.arkCodeId,
                view: props.type === 'view',
                allowClear: "",
                appId: "cos_ofbu"
              }, null, 8, ["cosId", "label", "value", "isEmpty", "disabled", "view"])
            ]),
            _: 1
          }, 16, ["label"]),
          _createVNode(_component_dc_form_item, _mergeProps({
            label: _unref(t)('business.7xsihs')
          }, _unref(validateInfos).status), {
            label: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('business.7xsihs')) + " ", 1),
              _createVNode(_component_a_tooltip, {
                placement: "topLeft",
                overlayStyle: { maxWidth: '800px' }
              }, {
                title: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(businessStatusEnumFilter.value, (item) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: item.value
                    }, [
                      _createElementVNode("p", null, _toDisplayString(item.label) + _toDisplayString(item.desc ? `：${_unref(t)(item.descLang)}` : ''), 1)
                    ]))
                  }), 128))
                ]),
                default: _withCtx(() => [
                  _createVNode(_unref(ExclamationCircleOutlined), { style: {"margin-left":"2px","font-size":"14px"} })
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              _createVNode(_component_CosSelect, {
                value: dataForm.status,
                "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((dataForm.status) = $event)),
                options: businessStatusEnumFilter.value,
                disabled: !isEdit.value,
                view: props.type === 'view',
                allowClear: ""
              }, null, 8, ["value", "options", "disabled", "view"])
            ]),
            _: 1
          }, 16, ["label"]),
          _createVNode(_component_dc_form_item, _mergeProps({
            label: _unref(t)('business.me6p1g')
          }, _unref(validateInfos).headDestinationCountryWarehouse), {
            default: _withCtx(() => [
              _createVNode(_component_ParamCodesSelect, {
                value: dataForm.headDestinationCountryWarehouse,
                "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((dataForm.headDestinationCountryWarehouse) = $event)),
                options: paramCodeData.value.ofbu_demand_info,
                disabled: !isEdit.value,
                view: props.type === 'view',
                allowClear: ""
              }, null, 8, ["value", "options", "disabled", "view"])
            ]),
            _: 1
          }, 16, ["label"]),
          (props.type === 'view')
            ? (_openBlock(), _createBlock(_component_dc_form_item, {
                key: 0,
                label: _unref(t)('business.u21f2s')
              }, {
                default: _withCtx(() => [
                  _createVNode(CosInput, {
                    value: dataForm.id,
                    "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((dataForm.id) = $event)),
                    view: ""
                  }, null, 8, ["value"])
                ]),
                _: 1
              }, 8, ["label"]))
            : _createCommentVNode("", true),
          (props.type === 'view')
            ? (_openBlock(), _createBlock(_component_dc_form_item, {
                key: 1,
                label: _unref(t)('common.createTime')
              }, {
                default: _withCtx(() => [
                  _createVNode(CosInput, {
                    value: dataForm.createTime,
                    "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => ((dataForm.createTime) = $event)),
                    view: ""
                  }, null, 8, ["value"])
                ]),
                _: 1
              }, 8, ["label"]))
            : _createCommentVNode("", true),
          (props.type !== 'view')
            ? (_openBlock(), _createBlock(_component_a_col, {
                key: 2,
                span: 6
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_1, [
                    _createVNode(_component_a_form_item, _mergeProps({
                      style: {"width":"70%"},
                      label: _unref(t)('business.kolh0f')
                    }, _unref(validateInfos).planSaleAmount), {
                      default: _withCtx(() => [
                        _createVNode(_component_CosInputNumber, {
                          value: dataForm.planSaleAmount,
                          "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((dataForm.planSaleAmount) = $event)),
                          maxlength: 15,
                          min: 0,
                          controls: false,
                          placeholder: _unref(t)('business.wsz78k'),
                          formatter: (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                          parser: (value) => value.replace(/\$\s?|(,*)/g, '')
                        }, null, 8, ["value", "placeholder", "formatter", "parser"])
                      ]),
                      _: 1
                    }, 16, ["label"]),
                    _createVNode(_component_a_form_item, _mergeProps({
                      label: "currency",
                      style: {"width":"30%"}
                    }, _unref(validateInfos).currency, { class: "hide-label" }), {
                      default: _withCtx(() => [
                        _createVNode(_component_ParamCodesSelect, {
                          options: paramCodeData.value.ofbu_currency,
                          value: dataForm.currency,
                          "onUpdate:value": _cache[10] || (_cache[10] = ($event: any) => ((dataForm.currency) = $event)),
                          allowClear: false
                        }, null, 8, ["options", "value"])
                      ]),
                      _: 1
                    }, 16)
                  ])
                ]),
                _: 1
              }))
            : (_openBlock(), _createBlock(_component_dc_form_item, _mergeProps({
                key: 3,
                label: _unref(t)('business.kolh0f')
              }, _unref(validateInfos).planSaleAmount), {
                default: _withCtx(() => [
                  _createVNode(_component_ParamCodesSelect, {
                    cosBeforeDes: dataForm.planSaleAmount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    options: paramCodeData.value.ofbu_currency,
                    value: dataForm.currency,
                    "onUpdate:value": _cache[11] || (_cache[11] = ($event: any) => ((dataForm.currency) = $event)),
                    view: props.type === 'view'
                  }, null, 8, ["cosBeforeDes", "options", "value", "view"])
                ]),
                _: 1
              }, 16, ["label"])),
          _createVNode(_component_dc_form_item, {
            label: _unref(t)('business.t3fsg4'),
            name: "shipments"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_ParamCodesSelect, {
                value: dataForm.shipments,
                "onUpdate:value": _cache[12] || (_cache[12] = ($event: any) => ((dataForm.shipments) = $event)),
                options: paramCodeData.value.ofbu_shipments,
                disabled: !isEdit.value,
                view: props.type === 'view',
                allowClear: ""
              }, null, 8, ["value", "options", "disabled", "view"])
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(_component_a_col, { span: 24 }),
          _createVNode(_component_dc_form_item, {
            label: _unref(t)('common.remark'),
            span: 12
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CosTextarea, {
                value: dataForm.remark,
                "onUpdate:value": _cache[13] || (_cache[13] = ($event: any) => ((dataForm.remark) = $event)),
                maxlength: 2000,
                disabled: !isEdit.value,
                view: props.type === 'view'
              }, null, 8, ["value", "disabled", "view"])
            ]),
            _: 1
          }, 8, ["label"])
        ]),
        _: 1
      }, 8, ["title"]),
      _createVNode(_component_dc_layout_card, {
        title: _unref(t)('business.23f4y5'),
        span: 6
      }, {
        default: _withCtx(() => [
          _createVNode(_component_dc_form_item, {
            label: _unref(t)('business.tw4fv1')
          }, {
            label: _withCtx(() => [
              _createVNode(_component_a_tooltip, {
                placement: "topLeft",
                overlayStyle: { maxWidth: '800px' }
              }, {
                title: _withCtx(() => [
                  _createElementVNode("p", null, _toDisplayString(_unref(t)('business.tw4fv1Desc')), 1)
                ]),
                default: _withCtx(() => [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)('business.tw4fv1')), 1),
                  _createVNode(_unref(ExclamationCircleOutlined), { style: {"margin-left":"2px","font-size":"14px"} })
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              _createVNode(_component_ParamCodesSelect, {
                cosBeforeDes: dataForm.avgMonthlySales?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                options: paramCodeData.value.ofbu_currency,
                value: dataForm.avgMonthlySalesCurrency,
                "onUpdate:value": _cache[14] || (_cache[14] = ($event: any) => ((dataForm.avgMonthlySalesCurrency) = $event)),
                disabled: "",
                view: ""
              }, null, 8, ["cosBeforeDes", "options", "value"])
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(_component_dc_form_item, {
            label: _unref(t)('business.ts2f2s')
          }, {
            label: _withCtx(() => [
              _createVNode(_component_a_tooltip, {
                placement: "topLeft",
                overlayStyle: { maxWidth: '800px' }
              }, {
                title: _withCtx(() => [
                  _createElementVNode("p", null, _toDisplayString(_unref(t)('business.ts2f2sDesc')), 1)
                ]),
                default: _withCtx(() => [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)('business.ts2f2s')), 1),
                  _createVNode(_unref(ExclamationCircleOutlined), { style: {"margin-left":"2px","font-size":"14px"} })
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              _createVNode(CosInput, {
                value: dataForm.avgMonthlyOrder,
                "onUpdate:value": _cache[15] || (_cache[15] = ($event: any) => ((dataForm.avgMonthlyOrder) = $event)),
                disabled: "",
                view: ""
              }, null, 8, ["value"])
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(_component_dc_form_item, {
            label: _unref(t)('business.sj39ks')
          }, {
            label: _withCtx(() => [
              _createVNode(_component_a_tooltip, {
                placement: "topLeft",
                overlayStyle: { maxWidth: '800px' }
              }, {
                title: _withCtx(() => [
                  _createElementVNode("p", null, _toDisplayString(_unref(t)('business.sj39ksDesc')), 1)
                ]),
                default: _withCtx(() => [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)('business.sj39ks')), 1),
                  _createVNode(_unref(ExclamationCircleOutlined), { style: {"margin-left":"2px","font-size":"14px"} })
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              _createVNode(CosSingleDateTimePicker, {
                isOutputBeijingTime: "",
                startTime: dataForm.firstInboundDate,
                "onUpdate:startTime": _cache[16] || (_cache[16] = ($event: any) => ((dataForm.firstInboundDate) = $event)),
                isRange: false,
                disabled: "",
                view: ""
              }, null, 8, ["startTime"])
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(_component_dc_form_item, {
            label: _unref(t)('business.sasd32')
          }, {
            label: _withCtx(() => [
              _createVNode(_component_a_tooltip, {
                placement: "topLeft",
                overlayStyle: { maxWidth: '800px' }
              }, {
                title: _withCtx(() => [
                  _createElementVNode("p", null, _toDisplayString(_unref(t)('business.sasd32Desc')), 1)
                ]),
                default: _withCtx(() => [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)('business.sasd32')), 1),
                  _createVNode(_unref(ExclamationCircleOutlined), { style: {"margin-left":"2px","font-size":"14px"} })
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              _createVNode(CosSingleDateTimePicker, {
                isOutputBeijingTime: "",
                startTime: dataForm.firstTransactionDate,
                "onUpdate:startTime": _cache[17] || (_cache[17] = ($event: any) => ((dataForm.firstTransactionDate) = $event)),
                isRange: false,
                disabled: "",
                view: ""
              }, null, 8, ["startTime"])
            ]),
            _: 1
          }, 8, ["label"])
        ]),
        _: 1
      }, 8, ["title"])
    ]),
    _: 1
  }))
}
}

})